import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, MapPin } from "lucide-react";
import { SITE, WA_DEFAULT, IMAGES } from "@/lib/site";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      data-testid="hero-title-line"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const TAGLINES = {
  EN: "Sacred journeys. Luxury wheels. Based in Rayagada, travelling all of Odisha and India — pilgrimage circuits, coastal escapes and hill retreats with a chauffeur-driven premium fleet.",
  OD: "ଜୟ ଜଗନ୍ନାଥ। ରାୟଗଡ଼ାରୁ ଓଡ଼ିଶା ଓ ସମଗ୍ର ଭାରତରେ ବିଳାସପୂର୍ଣ୍ଣ ଯାତ୍ରା — ତୀର୍ଥଯାତ୍ରା, ଭ୍ରମଣ ଓ ପ୍ରିମିୟମ କାର ସେବା।",
  HI: "जय जगन्नाथ। रायगड़ा से पूरे ओडिशा और भारत में लग्ज़री यात्राएँ — तीर्थ यात्रा, sightseeing और प्रीमियम कार सेवा।",
};

export default function Hero({ lang, onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="A scenic road journey through Odisha at golden hour"
          className="h-[115%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1418] via-[#0F1418]/35 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1418]/85 via-[#0F1418]/30 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-20 pt-40 sm:px-8 lg:px-16">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-6 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.3em] text-[#D4AF37]"
        >
          <MapPin className="h-3.5 w-3.5" /> Rayagada · Odisha · All India
        </motion.p>

        <h1 className="font-serif text-4xl font-bold leading-[1.06] tracking-tight sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.35}>Travel the sacred</MaskedLine>
          <MaskedLine delay={0.5}>
            land of <em className="not-italic text-[#D4AF37]">Jagannath</em>
          </MaskedLine>
          <MaskedLine delay={0.65}>
            in <span className="italic text-[#D4AF37]">quiet luxury.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          data-testid="hero-tagline"
          className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg"
        >
          {TAGLINES[lang]}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={onBook}
            data-testid="hero-book-btn"
            className="group flex items-center gap-2 rounded-full bg-[#D4AF37] px-7 py-3.5 text-sm font-semibold text-[#0F1418] transition-transform hover:scale-105"
          >
            Book Your Trip
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            className="flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-medium text-white/90 backdrop-blur-sm transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp {SITE.phoneDisplay}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-6"
        >
          {[
            ["6+", "Tour circuits"],
            ["4", "Fleet classes"],
            ["3", "Languages spoken"],
          ].map(([n, label]) => (
            <div key={label}>
              <p className="font-serif text-3xl font-semibold text-[#D4AF37]">{n}</p>
              <p className="mt-1 text-xs uppercase tracking-widest text-white/50">{label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
